import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  X, 
  Monitor, 
  Tablet, 
  Smartphone, 
  ExternalLink,
  Download,
  Share,
  Code
} from "lucide-react";
import { PageData } from "../VisualPageBuilder";
import ElementRenderer from "./ElementRenderer";

interface PreviewFrameProps {
  pageData: PageData;
  activeView: 'desktop' | 'tablet' | 'mobile';
  onClose: () => void;
}

export default function PreviewFrame({ pageData, activeView, onClose }: PreviewFrameProps) {
  // Ensure pageData and elements exist with proper defaults
  const defaultGlobalStyles = {
    colors: {
      primary: '#3b82f6',
      secondary: '#6b7280', 
      accent: '#10b981',
      background: '#ffffff',
      text: '#111827'
    },
    fonts: {
      primary: 'Inter',
      secondary: 'Inter'
    },
    spacing: {
      small: '8px',
      medium: '16px',
      large: '32px'
    }
  };
  
  const safePageData = {
    elements: pageData?.elements || [],
    globalStyles: {
      colors: { ...defaultGlobalStyles.colors, ...pageData?.globalStyles?.colors },
      fonts: { ...defaultGlobalStyles.fonts, ...pageData?.globalStyles?.fonts },
      spacing: { ...defaultGlobalStyles.spacing, ...pageData?.globalStyles?.spacing }
    }
  };
  const safeElements = safePageData.elements;

  const getFrameWidth = () => {
    switch (activeView) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '100%';
    }
  };

  const getFrameHeight = () => {
    switch (activeView) {
      case 'mobile':
        return '667px';
      case 'tablet':
        return '1024px';
      case 'desktop':
      default:
        return '100vh';
    }
  };

  // Apply global styles to CSS variables
  const globalCSSVars = {
    '--color-primary': safePageData.globalStyles.colors.primary,
    '--color-secondary': safePageData.globalStyles.colors.secondary,
    '--color-accent': safePageData.globalStyles.colors.accent,
    '--color-background': safePageData.globalStyles.colors.background,
    '--color-text': safePageData.globalStyles.colors.text,
    '--font-primary': safePageData.globalStyles.fonts.primary,
    '--font-secondary': safePageData.globalStyles.fonts.secondary,
    '--spacing-small': safePageData.globalStyles.spacing.small,
    '--spacing-medium': safePageData.globalStyles.spacing.medium,
    '--spacing-large': safePageData.globalStyles.spacing.large,
  } as React.CSSProperties;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Badge variant="outline">Vorschau</Badge>
                Landing Page Vorschau
              </DialogTitle>
              <DialogDescription>
                Sehen Sie Ihre Landing Page in verschiedenen Ansichten
              </DialogDescription>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Device Indicators */}
              <div className="flex items-center gap-1">
                <div className={`p-2 rounded ${activeView === 'desktop' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <Monitor className="w-4 h-4" />
                </div>
                <div className={`p-2 rounded ${activeView === 'tablet' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <Tablet className="w-4 h-4" />
                </div>
                <div className={`p-2 rounded ${activeView === 'mobile' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <Smartphone className="w-4 h-4" />
                </div>
              </div>

              {/* Action Buttons */}
              <Button variant="outline" size="sm">
                <Share className="w-4 h-4 mr-2" />
                Teilen
              </Button>
              
              <Button variant="outline" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Öffnen
              </Button>
              
              <Button variant="outline" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Preview Content */}
        <div className="flex-1 flex items-center justify-center bg-muted/30 p-6">
          <div 
            className="bg-background shadow-xl border rounded-lg overflow-hidden transition-all duration-300"
            style={{
              width: getFrameWidth(),
              height: getFrameHeight(),
              maxHeight: '80vh'
            }}
          >
            <div 
              className="h-full overflow-auto"
              style={globalCSSVars}
            >
              {/* Render Page Content */}
              <div className="min-h-full">
                {safeElements.length === 0 ? (
                  /* Empty State */
                  <div className="h-full flex items-center justify-center text-center p-8">
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
                        <Code className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium">Leere Seite</h3>
                        <p className="text-muted-foreground">
                          Fügen Sie Elemente hinzu, um Ihre Landing Page zu erstellen.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Page Elements */
                  <div>
                    {safeElements
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .filter(element => element.settings?.isVisible !== false)
                      .map((element) => (
                        <div key={element.id}>
                          <ElementRenderer
                            element={element}
                            globalStyles={safePageData.globalStyles}
                            activeView={activeView}
                            isSelected={false}
                            onUpdate={() => {}} // Read-only in preview
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Footer */}
        <div className="px-6 py-3 border-t bg-muted/50 text-center">
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span>Vorschau-Modus</span>
            <span>•</span>
            <span>{safeElements.length} Element{safeElements.length !== 1 ? 'e' : ''}</span>
            <span>•</span>
            <span className="capitalize">{activeView} Ansicht</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}